import React, {useState} from 'react';
import {Box, Button, Checkbox, FormControlLabel, IconButton, Popover, TableCell, TextField, Typography} from '@mui/material';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import FilterListIcon from '@material-ui/icons/FilterList';
import {isSortable} from './tableControls';

/* A table header cell with the shared sort and filter controls (QA, 9 Oct 2026).
   Spread `useTableControls(...).headerProps(key)` into it and pass the page's
   own `sx` (its `headCell` style) — the cell keeps the page's look.

     · click the cell      → none → ascending → descending → none
     · the filter icon     → a popover: a checklist for text/band columns,
                              a min–max pair for number/percent columns
   The filter icon stays hidden until the header is hovered (or the filter is
   active), so a header row reads as it did before. */

const NUMERIC = new Set(['number', 'percent']);
const ACCENT = '#FF6900';

const ChecklistFilter = ({options, value, onChange}) => {
    const [q, setQ] = useState('');
    const selected = value?.values instanceof Set ? value.values : new Set();
    const shown = q ? options.filter((o) => o.toLowerCase().includes(q.toLowerCase())) : options;
    const set = (next) => onChange(next.size ? {values: next} : null);
    return (
        <Box sx={{p: 1.5, minWidth: 220, maxWidth: 320}}>
            {options.length > 8 && (
                <TextField size="small" fullWidth placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} sx={{mb: 1}} inputProps={{'aria-label': 'Search options'}}/>
            )}
            <Box sx={{maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column'}}>
                {shown.map((o) => (
                    <FormControlLabel
                        key={o}
                        sx={{m: 0, '& .MuiFormControlLabel-label': {fontSize: '13px', color: '#374151'}}}
                        control={<Checkbox size="small" checked={selected.has(o)} onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(o); else next.delete(o);
                            set(next);
                        }}/>}
                        label={o}
                    />
                ))}
                {shown.length === 0 && <Typography sx={{fontSize: '13px', color: '#9CA3AF', p: 1}}>No match</Typography>}
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 1, gap: 1}}>
                <Box>
                    <Button size="small" onClick={() => set(new Set(options))} sx={{textTransform: 'none', fontSize: '12px'}}>All</Button>
                    <Button size="small" onClick={() => set(new Set())} sx={{textTransform: 'none', fontSize: '12px'}}>None</Button>
                </Box>
                <Button size="small" onClick={() => onChange(null)} disabled={!selected.size} sx={{textTransform: 'none', fontSize: '12px'}}>Clear</Button>
            </Box>
        </Box>
    );
};

const RangeFilter = ({range, value, onChange, unit}) => {
    const min = value?.min ?? '';
    const max = value?.max ?? '';
    const update = (patch) => {
        const next = {min, max, ...patch};
        const empty = (x) => x === '' || x === null || x === undefined;
        onChange(empty(next.min) && empty(next.max) ? null : next);
    };
    const suffix = unit === 'percent' ? ' %' : '';
    return (
        <Box sx={{p: 1.5, minWidth: 240}}>
            {range && <Typography sx={{fontSize: '12px', color: '#6A7282', mb: 1}}>Values from {range.min}{suffix} to {range.max}{suffix}</Typography>}
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                <TextField size="small" type="number" label={`Min${suffix}`} value={min} onChange={(e) => update({min: e.target.value})} inputProps={{'aria-label': 'Minimum'}}/>
                <Typography sx={{color: '#9CA3AF'}}>–</Typography>
                <TextField size="small" type="number" label={`Max${suffix}`} value={max} onChange={(e) => update({max: e.target.value})} inputProps={{'aria-label': 'Maximum'}}/>
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                <Button size="small" onClick={() => onChange(null)} disabled={min === '' && max === ''} sx={{textTransform: 'none', fontSize: '12px'}}>Clear</Button>
            </Box>
        </Box>
    );
};

export const SortFilterHeaderCell = ({col, sort, onSort, filter, onFilter, options = [], range = null, filterable = false, sx, align, children, ...cellProps}) => {
    const [anchor, setAnchor] = useState(null);
    const sortable = isSortable(col);
    const active = !!sort && sort.key === col.key;
    const filterOn = !!filter;
    const SortIcon = active ? (sort.dir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon) : UnfoldMoreIcon;
    const textAlign = align || sx?.textAlign || 'left';
    const justify = textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start';
    return (
        <TableCell
            {...cellProps}
            aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            onClick={sortable ? () => onSort(col.key) : undefined}
            sx={{
                ...sx,
                cursor: sortable ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap',
                '&:hover .dq-th-filter': {opacity: 1},
                '&:hover .dq-th-sort': {opacity: 1},
            }}
        >
            <Box sx={{display: 'inline-flex', alignItems: 'center', justifyContent: justify, gap: 0.4, width: '100%'}}>
                <Box component="span">{children ?? col.label}</Box>
                {sortable && (
                    <SortIcon className="dq-th-sort" style={{fontSize: 15, opacity: active ? 1 : 0.45, color: active ? ACCENT : 'inherit'}}/>
                )}
                {filterable && (
                    <IconButton
                        className="dq-th-filter"
                        size="small"
                        aria-label={`Filter ${col.label || col.key}`}
                        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                        sx={{p: 0.25, ml: 0.25, color: 'inherit', opacity: filterOn ? 1 : 0, transition: 'opacity .15s',
                            '&::after': filterOn ? {content: '""', position: 'absolute', top: 1, right: 1, width: 6, height: 6, borderRadius: '50%', backgroundColor: ACCENT} : undefined}}
                    >
                        <FilterListIcon style={{fontSize: 15}}/>
                    </IconButton>
                )}
            </Box>
            {filterable && (
                <Popover
                    open={!!anchor}
                    anchorEl={anchor}
                    onClose={() => setAnchor(null)}
                    onClick={(e) => e.stopPropagation()}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                    transformOrigin={{vertical: 'top', horizontal: 'left'}}
                    PaperProps={{sx: {borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)'}}}
                >
                    {NUMERIC.has(col.type)
                        ? <RangeFilter range={range} value={filter} onChange={onFilter} unit={col.type}/>
                        : <ChecklistFilter options={options} value={filter} onChange={onFilter}/>}
                </Popover>
            )}
        </TableCell>
    );
};

export default SortFilterHeaderCell;
